import { formatUserFacingError } from '@/lib/errors'
import { useState, useEffect, useCallback } from 'react'
import type { SectionLibraryDocument } from '@/types'
import { adminSectionLibraryService } from '../services/admin.sectionLibrary.service'
import type { SectionLibraryFormValues } from '../types'

interface UseAdminSectionLibraryResult {
  sections: Array<SectionLibraryDocument & { id: string }>
  loading: boolean
  error: string | null
  refresh: () => void
  updateMetadata: (id: string, data: SectionLibraryFormValues) => Promise<void>
  deleteSection: (id: string) => Promise<void>
}

export function useAdminSectionLibrary(): UseAdminSectionLibraryResult {
  const [sections, setSections] = useState<Array<SectionLibraryDocument & { id: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminSectionLibraryService
      .getAll()
      .then(setSections)
      .catch((err) => setError(formatUserFacingError(err, 'Failed to load section library')))
      .finally(() => setLoading(false))
  }, [tick])

  const updateMetadata = useCallback(async (id: string, data: SectionLibraryFormValues) => {
    await adminSectionLibraryService.updateMetadata(id, data)
    refresh()
  }, [refresh])

  const deleteSection = useCallback(async (id: string) => {
    await adminSectionLibraryService.delete(id)
    refresh()
  }, [refresh])

  return { sections, loading, error, refresh, updateMetadata, deleteSection }
}
